import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { FaComments, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { createChatSocket } from '../../utils/chatSocket';
import { graphqlRequest } from '../../utils/graphqlRequest';
import './AdminLiveChatWidget.css';

const LIVE_CHAT_ACCESS_QUERY = `
  query GetLiveChatAccess {
    getMyProfile {
      company {
        liveChatEnabled
        liveChatSupporterId
      }
    }
  }
`;

const isManagementRole = (roles = []) => {
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => {
    const normalized = String(role).toLowerCase().replace(/[\s_/-]+/g, '');
    return (
      normalized.includes('admin') ||
      normalized.includes('manager') ||
      normalized.includes('supervisor') ||
      normalized.includes('operationsmanager')
    );
  });
};

const buildContactFromConversation = (conversation, currentUserId) => {
  if (!conversation || conversation.type !== 'DIRECT') return null;
  const members = Array.isArray(conversation.members) ? conversation.members : [];
  const partner = members.find((member) => member?.id && member.id !== currentUserId);
  if (!partner?.id) return null;

  if (isManagementRole(partner.roles || [])) {
    return null;
  }

  return {
    id: partner.id,
    name:
      `${partner.firstName || ''} ${partner.lastName || ''}`.trim() ||
      partner.email ||
      'Employee',
    subtitle: partner.jobTitle || partner.email || 'Employee',
    conversationId: conversation.conversationId,
    lastMessageAt: conversation.lastMessageAt
      ? new Date(conversation.lastMessageAt).getTime()
      : 0,
  };
};

const getAttachmentLabel = (url, key, index) => {
  const source = key || url;
  if (!source) return `File ${index + 1}`;
  try {
    const pathname = source.startsWith('http') ? new URL(source).pathname : source;
    const parts = pathname.split('/');
    return decodeURIComponent(parts[parts.length - 1]) || `File ${index + 1}`;
  } catch {
    const parts = source.split('/');
    return parts[parts.length - 1] || `File ${index + 1}`;
  }
};

const getAttachmentPreviewType = (url, key) => {
  const source = String(key || url || '').toLowerCase().split('?')[0];
  if (!source) return 'file';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(source)) return 'image';
  if (/\.pdf$/.test(source)) return 'pdf';
  if (/\.(mp4|webm|ogg|mov|m4v)$/.test(source)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(source)) return 'audio';
  return 'file';
};

const AdminLiveChatWidget = () => {
  const token = Cookies.get('accessToken');
  const [showLiveChatFab, setShowLiveChatFab] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const socketRef = useRef(null);
  const listRef = useRef(null);
  const conversationIdRef = useRef('');
  const currentUserIdRef = useRef('');
  const selectedConversationIdRef = useRef('');

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      currentUserIdRef.current = decoded?.sub?.userId || '';
    } catch {
      currentUserIdRef.current = '';
    }
  }, [token]);

  const refreshLiveChatVisibility = useCallback(async () => {
    if (!token) {
      setShowLiveChatFab(false);
      return;
    }
    let userId = '';
    try {
      const decoded = jwtDecode(token);
      userId = String(decoded?.sub?.userId || '').trim();
    } catch {
      setShowLiveChatFab(false);
      return;
    }
    if (!userId) {
      setShowLiveChatFab(false);
      return;
    }
    try {
      const data = await graphqlRequest(LIVE_CHAT_ACCESS_QUERY);
      const company = data?.getMyProfile?.company;
      const enabled = Boolean(company?.liveChatEnabled);
      const supporterId = String(company?.liveChatSupporterId || '').trim();
      const isDesignatedSupporter =
        enabled &&
        supporterId.length > 0 &&
        supporterId.toLowerCase() === userId.toLowerCase();
      setShowLiveChatFab(isDesignatedSupporter);
    } catch {
      setShowLiveChatFab(false);
    }
  }, [token]);

  useEffect(() => {
    refreshLiveChatVisibility();
  }, [refreshLiveChatVisibility]);

  useEffect(() => {
    const onFocus = () => {
      refreshLiveChatVisibility();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshLiveChatVisibility]);

  useEffect(() => {
    const open = (event) => {
      const conversationId = event?.detail?.conversationId;
      if (conversationId) {
        selectedConversationIdRef.current = String(conversationId);
      }
      setOpen(true);
    };
    window.addEventListener('admin-live-chat-open', open);
    return () => window.removeEventListener('admin-live-chat-open', open);
  }, []);

  useEffect(() => {
    selectedConversationIdRef.current = selectedContact?.conversationId || '';
  }, [selectedContact?.conversationId]);

  const loadConversations = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit('getMyConversations', {}, (response = {}) => {
      const rows = Array.isArray(response?.conversations) ? response.conversations : [];
      const mapped = rows
        .map((conversation) =>
          buildContactFromConversation(conversation, currentUserIdRef.current),
        )
        .filter(Boolean)
        .sort((a, b) => Number(b.lastMessageAt || 0) - Number(a.lastMessageAt || 0));
      setContacts(mapped);
      const preferredConversationId = selectedConversationIdRef.current;
      const preferred = preferredConversationId
        ? mapped.find((item) => item.conversationId === preferredConversationId)
        : null;
      setSelectedContact(preferred || mapped[0] || null);
    });
  }, []);

  const openConversation = useCallback((conversationId) => {
    if (!socketRef.current || !conversationId) return;
    setIsLoadingHistory(true);
    setMessages([]);
    setError('');
    conversationIdRef.current = conversationId;
    socketRef.current.emit('joinConversation', { conversationId });
    socketRef.current.emit(
      'getMessages',
      { conversationId, query: { limit: 40 } },
      (history = {}) => {
        const rows = Array.isArray(history?.messages)
          ? [...history.messages].reverse()
          : [];
        setMessages(rows);
        setIsLoadingHistory(false);
      },
    );
  }, []);

  useEffect(() => {
    if (!open || !token) return undefined;

    const socket = createChatSocket(token);
    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('Connected');
      loadConversations();
    });

    socket.on('disconnect', () => setStatus('Disconnected'));
    socket.on('connect_error', (socketError) => {
      setStatus('Disconnected');
      setError(socketError?.message || 'Live chat unavailable');
    });

    socket.on('conversation.created', () => loadConversations());
    socket.on('notification.message', () => loadConversations());
    socket.on('message.created', (event = {}) => {
      if (!event?.conversationId) return;
      if (event.conversationId !== conversationIdRef.current) {
        loadConversations();
        return;
      }
      setMessages((prev) => {
        if (prev.some((msg) => msg.id === event.messageId)) return prev;
        return [
          ...prev,
          {
            id: event.messageId,
            senderId: event.senderId,
            senderName: event.senderName || '',
            content: event.content || '',
            attachments: event.attachmentKeys || [],
            attachmentUrls: event.attachmentUrls || [],
            mentionedUserIds: Array.isArray(event.mentionedUserIds)
              ? event.mentionedUserIds
              : [],
            createdAt: event.createdAt,
          },
        ];
      });
      loadConversations();
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      conversationIdRef.current = '';
      setMessages([]);
      setContacts([]);
      setSelectedContact(null);
      setStatus('Disconnected');
    };
  }, [loadConversations, open, token]);

  useEffect(() => {
    if (!selectedContact?.conversationId) return;
    openConversation(selectedContact.conversationId);
  }, [openConversation, selectedContact?.conversationId]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = () => {
    const text = content.trim();
    const conversationId = selectedContact?.conversationId;
    if (!text || !socketRef.current || !conversationId) return;
    socketRef.current.emit('sendMessage', {
      conversationId,
      content: text,
      attachmentKeys: [],
    });
    setContent('');
  };

  const formatTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const title = useMemo(() => {
    if (!selectedContact?.id) return 'Live Chat';
    return `Live Chat - ${selectedContact.name}`;
  }, [selectedContact?.id, selectedContact?.name]);

  // Allow Support Management pages to open the widget even if the FAB is hidden
  // (e.g. not the designated supporter, or the access check temporarily fails).
  if (!showLiveChatFab && !open) {
    return null;
  }

  return (
    <>
      {showLiveChatFab ? (
        <button
          type="button"
          className="admin-live-chat-fab"
          onClick={() => setOpen((prev) => !prev)}
          title="Live Chat"
        >
          <FaComments />
          <span>Live Chat</span>
        </button>
      ) : null}

      {open ? (
        <section className="admin-live-chat-window">
          <header className="admin-live-chat-head">
            <div>
              <h3>{title}</h3>
              <p>{status}</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} title="Close">
              <FaTimes />
            </button>
          </header>

          <div className="admin-live-chat-content">
            <aside className="admin-live-chat-contact-strip">
              <h4>Live Chat Inbox</h4>
              {contacts.length === 0 ? (
                <p>No live chat conversations yet.</p>
              ) : (
                contacts.map((contact) => (
                  <button
                    key={contact.conversationId}
                    type="button"
                    className={`admin-live-chat-contact ${
                      selectedContact?.conversationId === contact.conversationId
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <strong>{contact.name}</strong>
                    <span>{contact.subtitle}</span>
                  </button>
                ))
              )}
            </aside>

            <section className="admin-live-chat-panel">
              <div className="admin-live-chat-body" ref={listRef}>
                {!selectedContact?.conversationId ? (
                  <p className="admin-live-chat-empty">
                    Employees can message you from Help &amp; Support.
                  </p>
                ) : isLoadingHistory ? (
                  <p className="admin-live-chat-empty">Loading messages...</p>
                ) : messages.length === 0 ? (
                  <p className="admin-live-chat-empty">No messages yet.</p>
                ) : (
                  messages.map((msg) => {
                    const mine = msg.senderId === currentUserIdRef.current;
                    const mentionedMe =
                      !mine &&
                      Array.isArray(msg.mentionedUserIds) &&
                      msg.mentionedUserIds.includes(currentUserIdRef.current);
                    return (
                      <div key={msg.id} className={`admin-live-chat-row ${mine ? 'mine' : ''}`}>
                        <div
                          className={`admin-live-chat-bubble ${mine ? 'mine' : ''} ${
                            mentionedMe ? 'mentioned' : ''
                          }`}
                        >
                          {mentionedMe ? (
                            <span className="admin-live-chat-mention-chip">@You</span>
                          ) : null}
                          <p>{msg.content}</p>
                          {Array.isArray(msg.attachmentUrls) && msg.attachmentUrls.length > 0 ? (
                            <div className="admin-live-chat-attachments">
                              {msg.attachmentUrls.map((url, index) => {
                                const key = Array.isArray(msg.attachments)
                                  ? msg.attachments[index]
                                  : '';
                                const previewType = getAttachmentPreviewType(url, key);
                                const label = getAttachmentLabel(url, key, index);
                                return (
                                  <div key={`${msg.id}-attachment-${index}`} className="admin-live-chat-attachment-card">
                                    {previewType === 'image' ? (
                                      <img
                                        src={url}
                                        alt={label}
                                        className="admin-live-chat-attachment-image"
                                        loading="lazy"
                                      />
                                    ) : null}
                                    {previewType === 'pdf' ? (
                                      <iframe
                                        src={url}
                                        title={label}
                                        className="admin-live-chat-attachment-pdf"
                                      />
                                    ) : null}
                                    {previewType === 'video' ? (
                                      <video controls className="admin-live-chat-attachment-video">
                                        <source src={url} />
                                      </video>
                                    ) : null}
                                    {previewType === 'audio' ? (
                                      <audio controls className="admin-live-chat-attachment-audio">
                                        <source src={url} />
                                      </audio>
                                    ) : null}
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                      {label}
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                          <small>{formatTime(msg.createdAt)}</small>
                        </div>
                      </div>
                    );
                  })
                )}
                {error ? <p className="admin-live-chat-error">{error}</p> : null}
              </div>

              <footer className="admin-live-chat-input">
                <input
                  type="text"
                  placeholder="Reply in live chat..."
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  disabled={!selectedContact?.conversationId}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!selectedContact?.conversationId}
                >
                  <FaPaperPlane />
                </button>
              </footer>
            </section>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default AdminLiveChatWidget;
